// Rewarded-ad gate for Save / Share on the Result page.
// Free Android users must watch a rewarded ad before each save or share.
// Paid users, web, iOS — bypassed.
import { isAndroidApp } from "./platform";
import { showRewardedAd } from "./native-ads";
import { isAdminTestAds } from "./admin-test-ads";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Tiny in-memory cache so rapid Save/Share taps don't re-query the DB.
const paidCache = new Map<string, { paid: boolean; at: number }>();
const TTL_MS = 60_000;

async function isPaidUser(userId: string): Promise<boolean> {
  const cached = paidCache.get(userId);
  if (cached && Date.now() - cached.at < TTL_MS) return cached.paid;
  try {
    const { data } = await supabase
      .from("profiles")
      .select("credits_purchased_total")
      .eq("user_id", userId)
      .maybeSingle();
    const paid = Number(data?.credits_purchased_total ?? 0) > 0;
    paidCache.set(userId, { paid, at: Date.now() });
    return paid;
  } catch {
    return false;
  }
}

/**
 * Returns true if the caller may proceed with the save / share.
 * Shows a toast and returns false when the ad fails to fill or the user
 * dismisses it early.
 */
export async function runSaveShareAdGate(userId: string | undefined | null): Promise<boolean> {
  if (!isAndroidApp()) return true;
  if (!userId) return true;
  // Admin Test Ads mode forces the gate to run for every user (incl. paid/admin)
  // so the placement can be verified end-to-end.
  if (!isAdminTestAds() && await isPaidUser(userId)) return true;

  const result = await showRewardedAd({ userId });
  if (result.rewarded) return true;

  toast.error("Ad unavailable", {
    description: "Please try again in a moment — watching a quick ad unlocks save & share.",
  });
  return false;
}
